// AvatarDropzone — drag-and-drop file upload for an agent's avatar.
// Click anywhere on the zone or drop a file to upload. Posts multipart
// to `/agents/:id/avatar` and calls `onUploaded(agent)` with the
// refreshed agent JSON. SVGs are accepted as-is (the backend serves
// them raw); raster images get a 256x256 variant on the server.
//
// Three visual states:
//   • empty       → dashed dropzone with placeholder + "Upload" label
//   • has-avatar  → preview + Replace button + Remove (X) button
//   • dragging    → highlighted ring while a file is over the zone
//   • uploading   → spinner on the preview; UI disabled
import { useRef, useState } from "react"
import type { ChangeEvent, DragEvent } from "react"
import { Loader2, Trash2, Upload } from "lucide-react"
import { cn } from "@/lib/cn"
import type { Agent } from "@/types/agent"

const ACCEPT = "image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
const MAX_BYTES = 8 * 1024 * 1024 // 8MB

function csrfToken(): string {
  return document.querySelector<HTMLMetaElement>("meta[name=csrf-token]")?.content ?? ""
}

type Props = {
  agentId:    number
  agentName:  string
  avatarUrl:  string | null
  onUploaded: (agent: Agent) => void
  className?: string
}

export function AvatarDropzone({ agentId, agentName, avatarUrl, onUploaded, className }: Props) {
  const [dragOver, setDragOver]     = useState(false)
  const [uploading, setUploading]   = useState(false)
  const [error, setError]           = useState<string | null>(null)
  const fileInputRef                 = useRef<HTMLInputElement | null>(null)

  function pick() {
    if (uploading) return
    fileInputRef.current?.click()
  }

  function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (file) void upload(file)
    e.target.value = "" // allow re-selecting the same file
  }

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault()
    e.stopPropagation()
    setDragOver(false)
    const file = e.dataTransfer.files?.[0]
    if (file) void upload(file)
  }

  function handleDragOver(e: DragEvent<HTMLDivElement>) {
    e.preventDefault()
    e.stopPropagation()
    if (!dragOver) setDragOver(true)
  }

  function handleDragLeave(e: DragEvent<HTMLDivElement>) {
    e.preventDefault()
    e.stopPropagation()
    setDragOver(false)
  }

  async function upload(file: File) {
    setError(null)
    if (!ACCEPT.split(",").includes(file.type)) {
      setError(`Unsupported format (${file.type || "unknown"}). Use PNG, JPEG, WebP, GIF, or SVG.`)
      return
    }
    if (file.size > MAX_BYTES) {
      setError(`File too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Max 8MB.`)
      return
    }

    setUploading(true)
    try {
      const fd = new FormData()
      fd.append("avatar", file)
      const res = await fetch(`/agents/${agentId}/avatar`, {
        method:      "POST",
        body:        fd,
        credentials: "same-origin",
        headers:     { "X-CSRF-Token": csrfToken(), Accept: "application/json" },
      })
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { error?: string }
        setError(data.error ?? `Upload failed (${res.status})`)
        return
      }
      const updated = (await res.json()) as Agent
      onUploaded(updated)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setUploading(false)
    }
  }

  async function remove() {
    if (uploading) return
    if (!confirm(`Remove avatar for ${agentName}?`)) return
    setUploading(true)
    setError(null)
    try {
      const res = await fetch(`/agents/${agentId}/avatar`, {
        method:      "DELETE",
        credentials: "same-origin",
        headers:     { "X-CSRF-Token": csrfToken(), Accept: "application/json" },
      })
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { error?: string }
        setError(data.error ?? `Remove failed (${res.status})`)
        return
      }
      const updated = (await res.json()) as Agent
      onUploaded(updated)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setUploading(false)
    }
  }

  const initials = agentName.trim().slice(0, 1).toUpperCase() || "?"
  const hasAvatar = !!avatarUrl

  return (
    <div className={cn("space-y-2", className)}>
      <div
        role="button"
        tabIndex={0}
        onClick={pick}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); pick() } }}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragEnter={handleDragOver}
        onDragLeave={handleDragLeave}
        aria-label="Upload avatar"
        aria-busy={uploading}
        className={cn(
          "group flex items-center gap-4 rounded-control border-2 border-dashed p-3 transition outline-none",
          dragOver
            ? "border-brand-red bg-brand-redSoft"
            : "border-border bg-white/[0.02] hover:border-app-muted hover:bg-white/[0.04]",
          uploading && "pointer-events-none opacity-70",
          "focus-visible:border-brand-red",
        )}
      >
        {/* Preview chip */}
        <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-full border border-border bg-app-panel2">
          {hasAvatar ? (
            <img src={avatarUrl ?? ""} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-xl font-semibold text-app-muted">
              {initials}
            </div>
          )}
          {uploading && (
            <div className="absolute inset-0 flex items-center justify-center bg-app-bg/70">
              <Loader2 size={18} className="animate-spin text-app-text" />
            </div>
          )}
        </div>

        {/* Copy + actions */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Upload size={14} className="text-app-muted" />
            <span className="text-sm text-app-text">
              {hasAvatar ? "Replace avatar" : "Upload avatar"}
            </span>
          </div>
          <p className="mt-0.5 text-xs text-app-muted">
            Drop a file or click to browse. PNG, JPEG, WebP, GIF, or SVG. Max 8MB.
          </p>
        </div>

        {hasAvatar && !uploading && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); void remove() }}
            title="Remove avatar"
            aria-label="Remove avatar"
            className="rounded-control p-1.5 text-app-subtle transition hover:bg-white/5 hover:text-status-error"
          >
            <Trash2 size={16} />
          </button>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT}
          className="hidden"
          onChange={handleFileChange}
        />
      </div>

      {error && (
        <p className="text-xs text-status-error">{error}</p>
      )}
    </div>
  )
}
